/**
 * SitePick front — 購入前の相談チャット（/ask/*。custom-chat spec §9.2）。
 *
 * - 会話画面の poll: 5 秒 → 3 分操作なしで 15 秒 → visibilitychange:hidden で停止・再表示で即 1 回。
 *   AbortController で同時 1 本。429 は Retry-After。失敗は指数バックオフ（最大 60 秒）+「接続を確認しています」。
 * - 送信は fetch POST（nonce 同梱）。JS が無くても form POST + 303 で同じ操作ができる。
 * - 本文は textContent で描く。URL だけここで <a rel="noopener nofollow ugc" target="_blank"> にする（XSS 面を 1 か所に）。
 * - 開始フォーム: 送信前に本文を sessionStorage（sp_ask_draft）に残す。失敗して戻ってきたら /contact/ へ案内し、
 *   contact.js が本文を prefill する（storage が使えなくても動く）。
 * - 秒・残り分のカウントダウンは出さない。価格・在庫の判定はしない（商品の状態文言はサーバの値を描くだけ）。
 * - 相談種別 radio（販売導線 v2 Phase 1。docs/18 §2.3）: semiorder に切り替えたとき、本文が未編集（空 / 初期文のまま）なら初期文
 *   （data-sp-prefill。定義元は plugin の AskMessages）を入れ、general に戻したときは初期文のままなら空にする。編集済みの本文は触らない。
 * - 会話画面で ct / dx 付き URL（別商品の入口から来た）から送信できたら、URL から ct / dx を外す（リロードで初期文が再び入らないように。p は残す）。
 */

const DRAFT_KEY = "sp_ask_draft";
const PENDING_KEY = "sp_ask_pending";
const FAST_MS = 5000;
const SLOW_MS = 15000;
const IDLE_MS = 180000;
const MAX_BACKOFF_MS = 60000;
const URL_RE = /(https?:\/\/[^\s<>"']+)/g;

/**
 * 送信できたら、入口から引き継いだ ct / dx / src を composer と URL から外す（p は残す = 以後も同じ商品文脈で送る）。
 *
 * @param {HTMLFormElement} form
 */
function dropEntryParams(form) {
  ["ct", "dx", "src"].forEach((name) => {
    const el = form.querySelector(`input[type="hidden"][name="${name}"]`);
    if (el) {
      el.remove();
    }
  });
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has("ct") || url.searchParams.has("dx") || url.searchParams.has("src")) {
      url.searchParams.delete("ct");
      url.searchParams.delete("dx");
      url.searchParams.delete("src");
      window.history.replaceState(window.history.state, "", url.toString());
    }
  } catch {
    // URL が扱えない環境では何もしない。
  }
}

/**
 * sessionStorage（使えなければ何もしない）。
 */
const store = {
  get(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      /* 使えなくても動く */
    }
  },
  remove(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      /* 使えなくても動く */
    }
  },
};

/**
 * テキストを描き、URL だけリンクにする（innerHTML は使わない）。
 *
 * @param {HTMLElement} el
 * @param {string} text
 */
export function renderText(el, text) {
  el.textContent = "";
  const lines = String(text).split("\n");
  lines.forEach((line, i) => {
    let last = 0;
    line.replace(URL_RE, (match, _url, offset) => {
      if (offset > last) {
        el.appendChild(document.createTextNode(line.slice(last, offset)));
      }
      const a = document.createElement("a");
      a.href = match;
      a.textContent = match;
      a.rel = "noopener nofollow ugc";
      a.target = "_blank";
      el.appendChild(a);
      last = offset + match.length;
      return match;
    });
    if (last < line.length) {
      el.appendChild(document.createTextNode(line.slice(last)));
    }
    if (i < lines.length - 1) {
      el.appendChild(document.createElement("br"));
    }
  });
}

/**
 * サーバ描画済みの本文（esc_html + nl2br）の URL をリンクにする。
 *
 * @param {HTMLElement} el
 */
function linkifyExisting(el) {
  renderText(el, el.innerText);
}

/**
 * 入力欄の自動伸長（1〜6 行）。
 *
 * @param {HTMLTextAreaElement} input
 */
function autoGrow(input) {
  const line = parseFloat(getComputedStyle(input).lineHeight) || 22;
  input.style.height = "auto";
  input.style.height = `${Math.min(input.scrollHeight + 2, line * 6 + 24)}px`;
}

/**
 * 開始フォーム。
 *
 * @param {HTMLFormElement} form
 */
function initStart(form) {
  const status = form.querySelector("[data-sp-ask-status]");
  const input = form.querySelector(".js-sp-ask-input");
  if (input instanceof HTMLTextAreaElement) {
    autoGrow(input);
    input.addEventListener("input", () => autoGrow(input));
  }
  initTypeChoice(form, input instanceof HTMLTextAreaElement ? input : null);
  // 前回の送信が完了しなかった（ネットワーク失敗で戻ってきた）ら /contact/ へ案内する。
  const showFailed = () => {
    if (store.get(PENDING_KEY) !== "1" || !status) {
      return;
    }
    store.remove(PENDING_KEY);
    const texts = (window.SITEPICK && window.SITEPICK.messages && window.SITEPICK.messages.ask) || {};
    status.hidden = false;
    status.textContent = "";
    status.appendChild(document.createTextNode(`${texts.startFailed || "送信できませんでした。お問い合わせフォームからご相談ください。"} `));
    const a = document.createElement("a");
    a.className = "c-link";
    a.href = form.getAttribute("data-sp-contact") || "/contact/";
    a.textContent = texts.contactGuide || "お問い合わせフォーム";
    status.appendChild(a);
    const draft = store.get(DRAFT_KEY);
    if (input instanceof HTMLTextAreaElement && draft && !input.value) {
      input.value = draft;
    }
  };
  showFailed();
  window.addEventListener("pageshow", showFailed);
  form.addEventListener("submit", () => {
    if (input instanceof HTMLTextAreaElement && input.value.trim()) {
      store.set(DRAFT_KEY, input.value);
      store.set(PENDING_KEY, "1");
    }
    form.classList.add("is-loading");
  });
}

/**
 * 開始フォームの相談種別（radio）。本文は「未編集のときだけ」差し替える（ユーザーの入力を勝手に上書きしない）。
 *
 * @param {HTMLFormElement} form
 * @param {HTMLTextAreaElement|null} input
 */
function initTypeChoice(form, input) {
  const radios = Array.from(form.querySelectorAll('input[name="ct"]'));
  if (radios.length === 0) {
    return;
  }
  const prefill = (form.getAttribute("data-sp-prefill") || "").trim();
  const note = form.querySelector(".js-sp-ask-type-note");
  const title = document.querySelector(".js-sp-ask-title");
  const isUntouched = () => {
    if (!(input instanceof HTMLTextAreaElement)) {
      return false;
    }
    const v = input.value.trim();
    return v === "" || (prefill !== "" && v === prefill);
  };
  const apply = (type) => {
    const semi = type === "semiorder";
    if (note instanceof HTMLElement) {
      note.hidden = !semi;
    }
    if (title instanceof HTMLElement) {
      const t = title.getAttribute(semi ? "data-sp-title-semiorder" : "data-sp-title-general");
      if (t) {
        title.textContent = t;
      }
    }
    if (input instanceof HTMLTextAreaElement && isUntouched()) {
      input.value = semi ? prefill : "";
      autoGrow(input);
    }
  };
  radios.forEach((r) => {
    r.addEventListener("change", () => {
      if (r.checked) {
        apply(r.value);
      }
    });
  });
}

/**
 * 会話画面。
 *
 * @param {HTMLElement} page
 */
function initThread(page) {
  const cfgEl = document.getElementById("sp-ask-config");
  let cfg;
  try {
    cfg = JSON.parse(cfgEl ? cfgEl.textContent : "{}") || {};
  } catch {
    return;
  }
  // 開始できた = 下書きは不要。
  store.remove(DRAFT_KEY);
  store.remove(PENDING_KEY);

  const texts = cfg.texts || {};
  const timeline = page.querySelector(".js-sp-ask-timeline");
  const stateBox = page.querySelector(".js-sp-ask-state");
  const note = page.querySelector(".js-sp-ask-note");
  const next = page.querySelector(".js-sp-ask-next");
  const bar = page.querySelector(".js-sp-ask-bar");
  const soft = page.querySelector(".js-sp-ask-soft");
  const form = page.querySelector(".js-sp-ask-form");
  const input = page.querySelector(".js-sp-ask-input");
  const errorEl = page.querySelector(".js-sp-ask-error");
  const productStatus = page.querySelector(".js-sp-ask-product-status");

  page.querySelectorAll(".js-sp-ask-body").forEach((el) => linkifyExisting(el));
  if (timeline && typeof timeline.lastElementChild?.scrollIntoView === "function") {
    timeline.lastElementChild.scrollIntoView({ block: "end" });
  }

  let status = String(cfg.status || "");
  let reason = String(cfg.reason || "");
  let lastId = Number(cfg.lastId) || 0;
  let etag = "";
  let timer = 0;
  let ctrl = null;
  let backoff = 0;
  let lastAction = Date.now();
  let serverMin = FAST_MS;

  const handoffText = (r) => {
    const map = {
      UNAVAILABLE: texts.handoffUnavailable,
      DECLINED: texts.handoffDeclined,
      TIMED_OUT_INITIAL: texts.handoffTimedOut,
      IDLE_ADMIN: texts.handoffIdleAdmin,
      IDLE_VISITOR: texts.handoffIdleVisitor,
      ADMIN_SWITCH: texts.handoffAdminSwitch,
    };
    return map[r] || texts.slaSentence || "";
  };

  const renderState = (waitingFor) => {
    if (stateBox) {
      stateBox.setAttribute("data-sp-status", status);
      const waiting = stateBox.querySelector(".p-ask__waiting");
      let line = stateBox.querySelector(".p-ask__handoff");
      if (status !== "WAITING" && waiting) {
        waiting.remove();
      }
      if (status === "HANDOFF") {
        if (!line) {
          line = document.createElement("p");
          line.className = "p-ask__handoff";
          stateBox.insertBefore(line, stateBox.firstChild);
        }
        line.textContent = handoffText(reason);
      } else if (status === "ACTIVE" && line) {
        line.remove();
      }
    }
    if (note) {
      if (status === "ACTIVE" && waitingFor === "admin") {
        note.textContent = texts.waitingAdmin || "";
      } else if (status === "ACTIVE" && waitingFor === "visitor") {
        note.textContent = texts.waitingVisitor || "";
      } else {
        note.textContent = "";
      }
    }
    if (soft && status !== "WAITING") {
      soft.hidden = true;
    }
  };

  const appendMessage = (m) => {
    if (!timeline || m.id <= lastId) {
      return;
    }
    const art = document.createElement("article");
    art.className = `p-ask__msg p-ask__msg--${m.sender}`;
    art.setAttribute("data-sp-id", String(m.id));
    const meta = document.createElement("p");
    meta.className = "p-ask__msg-meta";
    const who = document.createElement("span");
    who.className = "p-ask__msg-sender";
    who.textContent = m.sender === "visitor" ? texts.you || "あなた" : m.sender === "admin" ? texts.staff || "SITE PICK" : texts.system || "";
    const at = document.createElement("time");
    at.textContent = m.at || "";
    meta.append(who, at);
    const body = document.createElement("div");
    body.className = "p-ask__msg-body";
    renderText(body, m.body || "");
    art.append(meta, body);
    timeline.appendChild(art);
    lastId = m.id;
    if (m.sender === "admin" && next) {
      next.hidden = false;
    }
    art.scrollIntoView({ block: "end", behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  };

  const interval = () => {
    if (backoff > 0) {
      return backoff;
    }
    const idle = Date.now() - lastAction > IDLE_MS;
    return Math.max(serverMin, idle ? SLOW_MS : FAST_MS);
  };

  const schedule = () => {
    window.clearTimeout(timer);
    if (document.visibilityState === "hidden" || status === "CLOSED") {
      return;
    }
    timer = window.setTimeout(poll, interval());
  };

  const showSoft = () => {
    if (!soft || status !== "WAITING" || !cfg.createdAt) {
      return;
    }
    const created = Date.parse(`${cfg.createdAt.replace(" ", "T")}Z`);
    if (!Number.isNaN(created) && Date.now() - created > 120000) {
      soft.hidden = false;
    }
  };

  function poll() {
    if (ctrl) {
      ctrl.abort();
    }
    ctrl = new AbortController();
    const headers = { "X-SitePick-Fetch": "1" };
    if (etag) {
      headers["If-None-Match"] = etag;
    }
    fetch(`${cfg.pollUrl}?after=${lastId}`, { credentials: "same-origin", signal: ctrl.signal, headers, cache: "no-store" })
      .then((res) => {
        if (res.status === 304) {
          return null;
        }
        if (res.status === 429) {
          const ra = Number(res.headers.get("Retry-After")) || 30;
          throw Object.assign(new Error("rate"), { retry: ra * 1000 });
        }
        if (res.status === 403) {
          window.location.reload();
          return null;
        }
        if (!res.ok) {
          throw new Error(`http ${res.status}`);
        }
        etag = res.headers.get("ETag") || "";
        return res.json();
      })
      .then((data) => {
        backoff = 0;
        if (bar) {
          bar.hidden = true;
        }
        if (data) {
          (data.messages || []).forEach(appendMessage);
          if (typeof data.poll_after_ms === "number") {
            serverMin = data.poll_after_ms;
          }
          if (data.product && productStatus) {
            productStatus.textContent = data.product.status === "SOLD" ? "このサイトは他社が購入しました。" : data.product.lock_label || "";
          }
          if (data.status !== status || (data.handoff_reason || "") !== reason) {
            status = data.status;
            reason = data.handoff_reason || "";
            if (status === "CLOSED") {
              window.location.reload();
              return;
            }
          }
          renderState(data.waiting_for);
        }
        showSoft();
        schedule();
      })
      .catch((err) => {
        if (err && err.name === "AbortError") {
          return;
        }
        backoff = err && err.retry ? err.retry : Math.min(MAX_BACKOFF_MS, backoff ? backoff * 2 : FAST_MS * 2);
        if (bar) {
          bar.hidden = false;
        }
        schedule();
      });
  }

  const markActive = () => {
    lastAction = Date.now();
  };
  ["keydown", "pointerdown", "touchstart", "scroll"].forEach((ev) => window.addEventListener(ev, markActive, { passive: true }));

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      poll();
    } else {
      window.clearTimeout(timer);
      if (ctrl) {
        ctrl.abort();
      }
    }
  });

  if (input instanceof HTMLTextAreaElement && form instanceof HTMLFormElement) {
    autoGrow(input);
    input.addEventListener("input", () => autoGrow(input));
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        form.requestSubmit ? form.requestSubmit() : form.submit();
      }
    });
    form.addEventListener("submit", (e) => {
      if (typeof fetch !== "function") {
        return;
      }
      e.preventDefault();
      if (form.classList.contains("is-loading") || !input.value.trim()) {
        return;
      }
      form.classList.add("is-loading");
      if (errorEl) {
        errorEl.hidden = true;
      }
      const body = new URLSearchParams(new FormData(form));
      fetch(cfg.actionUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: { "X-SitePick-Fetch": "1", "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      })
        .then((res) => res.json().catch(() => ({ ok: false, code: `http_${res.status}` })))
        .then((res) => {
          form.classList.remove("is-loading");
          if (res && res.ok) {
            input.value = "";
            autoGrow(input);
            markActive();
            poll();
            dropEntryParams(form);
            return;
          }
          if (res && (res.code === "closed" || res.code === "limit")) {
            window.location.reload();
            return;
          }
          if (errorEl) {
            errorEl.hidden = false;
            errorEl.textContent = res && res.code === "rate_limited" ? texts.rateLimited || "" : texts.sendFailed || "";
          }
        })
        .catch(() => {
          form.classList.remove("is-loading");
          if (errorEl) {
            errorEl.hidden = false;
            errorEl.textContent = texts.sendFailed || "";
          }
        });
    });
  }

  page.querySelectorAll("form.js-sp-ask-aux").forEach((f) => {
    f.addEventListener("submit", (e) => {
      const msg = f.getAttribute("data-sp-confirm");
      if (msg && !window.confirm(msg)) {
        e.preventDefault();
      }
    });
  });

  renderState(cfg.waitingFor);
  showSoft();
  schedule();
}

/**
 * /ask/* の画面（`.js-sp-ask` が無ければ何もしない）。
 */
export function initAsk() {
  const page = document.querySelector(".js-sp-ask");
  if (!page) {
    return;
  }
  const start = page.querySelector(".js-sp-ask-start");
  if (start instanceof HTMLFormElement) {
    initStart(start);
  }
  if (document.getElementById("sp-ask-config")) {
    initThread(page);
  }
}
